import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { debounce } from 'lodash';
import './AutocompleteDropdown.scss';

/**
 * AutocompleteDropdown - Combines ghost text autocomplete with dropdown selection
 *
 * Features:
 * - Ghost text shows best match inline as user types
 * - Dropdown shows all matching options
 * - Arrow keys navigate dropdown and update ghost text
 * - Tab/Enter accepts current selection
 * - Supports async data sources
 * - Debounced filtering for performance
 *
 * @param {string} value - Current input value
 * @param {function} onChange - Called with new value when selection changes
 * @param {array} suggestions - Array of suggestion strings
 * @param {string} placeholder - Placeholder text when empty
 * @param {boolean} disabled - Whether input is disabled
 * @param {boolean} loading - Show loading state
 * @param {string} className - Additional CSS classes
 * @param {function} onFocus - Called when input gains focus
 * @param {function} onBlur - Called when input loses focus
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 150)
 */
const AutocompleteDropdown = ({
  value = '',
  onChange,
  suggestions = [],
  placeholder = 'Enter value',
  disabled = false,
  loading = false,
  className = '',
  onFocus,
  onBlur,
  autoFocus = false,
  debounceMs = 150,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [inputValue, setInputValue] = useState(value);
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(value);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const wrapperRef = useRef(null);
  const isFocusedRef = useRef(false); // Track focus synchronously to prevent dropdown flash
  const blurTimestampRef = useRef(0); // Track when blur happened to ignore immediate refocus

  // Debounced function to update search value for filtering
  const debouncedSetSearch = useMemo(
    () =>
      debounce((val) => {
        setDebouncedSearchValue(val);
      }, debounceMs),
    [debounceMs]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  // Sync inputValue with external value prop
  useEffect(() => {
    setInputValue(value);
    setDebouncedSearchValue(value);
  }, [value]);

  // Filter suggestions based on debounced input (for dropdown list)
  const filteredSuggestions = useMemo(() => {
    if (!debouncedSearchValue) return suggestions;
    const lowerInput = debouncedSearchValue.toLowerCase();
    return suggestions.filter(s =>
      s.toLowerCase().includes(lowerInput)
    );
  }, [debouncedSearchValue, suggestions]);

  // Sort filtered suggestions - exact prefix matches first, then by position of match
  const sortedSuggestions = useMemo(() => {
    if (!inputValue) return filteredSuggestions;
    const lowerInput = inputValue.toLowerCase();

    return [...filteredSuggestions].sort((a, b) => {
      const aLower = a.toLowerCase();
      const bLower = b.toLowerCase();
      const aStartsWith = aLower.startsWith(lowerInput);
      const bStartsWith = bLower.startsWith(lowerInput);

      // Prefix matches come first
      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;

      // Then sort by position of match
      const aIndex = aLower.indexOf(lowerInput);
      const bIndex = bLower.indexOf(lowerInput);
      if (aIndex !== bIndex) return aIndex - bIndex;

      // Finally alphabetically
      return a.localeCompare(b);
    });
  }, [filteredSuggestions, inputValue]);

  // Ghost text - the completion part only (after what user typed)
  const ghostText = useMemo(() => {
    if (!inputValue || sortedSuggestions.length === 0) return '';

    const highlighted = sortedSuggestions[highlightedIndex];
    if (!highlighted) return '';

    const lowerInput = inputValue.toLowerCase();
    const lowerHighlighted = highlighted.toLowerCase();

    // Only show ghost if the highlighted item starts with what user typed
    if (lowerHighlighted.startsWith(lowerInput)) {
      return highlighted.slice(inputValue.length);
    }

    return '';
  }, [inputValue, sortedSuggestions, highlightedIndex]);

  // Reset highlighted index when suggestions change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [sortedSuggestions.length, inputValue]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && listRef.current) {
      const highlightedEl = listRef.current.children[highlightedIndex];
      if (highlightedEl) {
        highlightedEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const acceptSuggestion = useCallback((suggestion) => {
    setInputValue(suggestion);
    setDebouncedSearchValue(suggestion); // Immediately update without debounce
    debouncedSetSearch.cancel(); // Cancel any pending debounce
    onChange(suggestion);
    setIsOpen(false);
    inputRef.current?.focus();
  }, [onChange, debouncedSetSearch]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    setHighlightedIndex(0);
    // Debounce the search/filter update for performance
    debouncedSetSearch(newValue);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex(prev =>
            Math.min(prev + 1, sortedSuggestions.length - 1)
          );
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex(prev => Math.max(prev - 1, 0));
        }
        break;

      case 'Tab':
        // Accept ghost text if available
        if (ghostText && isOpen) {
          e.preventDefault();
          acceptSuggestion(sortedSuggestions[highlightedIndex]);
        }
        break;

      case 'Enter':
        e.preventDefault();
        if (isOpen && sortedSuggestions.length > 0) {
          acceptSuggestion(sortedSuggestions[highlightedIndex]);
        } else {
          // Accept current input value
          onChange(inputValue);
          setIsOpen(false);
        }
        break;

      case 'Escape':
        setIsOpen(false);
        // Revert to original value
        setInputValue(value);
        break;

      case 'ArrowRight':
        // Accept ghost text if cursor is at end of input
        if (ghostText && e.target.selectionStart === inputValue.length) {
          e.preventDefault();
          acceptSuggestion(sortedSuggestions[highlightedIndex]);
        }
        break;

      default:
        break;
    }
  };

  const handleFocus = (e) => {
    // Ignore focus events that happen within 100ms of blur (prevents modal refocus issues)
    if (Date.now() - blurTimestampRef.current < 100) {
      return;
    }
    isFocusedRef.current = true;
    // Don't auto-open dropdown on focus - wait for user to type or click arrow
    // This prevents unwanted dropdown opening from modal focus management
    onFocus?.(e);
  };

  const handleBlur = (e) => {
    // Record blur timestamp to prevent immediate refocus from reopening dropdown
    blurTimestampRef.current = Date.now();
    // Mark as not focused immediately (ref updates synchronously)
    // Note: Dropdown items use onMouseDown preventDefault to avoid triggering blur
    isFocusedRef.current = false;
    setIsOpen(false);
    // Always save the current input value on blur
    if (inputValue !== value) {
      onChange(inputValue);
    }
    onBlur?.(e);
  };

  const handleItemClick = (suggestion) => {
    acceptSuggestion(suggestion);
  };

  const handleItemMouseEnter = (index) => {
    setHighlightedIndex(index);
  };

  // Highlight matching text in suggestion
  const renderHighlightedText = (text) => {
    if (!inputValue) return text;

    const lowerText = text.toLowerCase();
    const lowerInput = inputValue.toLowerCase();
    const matchIndex = lowerText.indexOf(lowerInput);

    if (matchIndex === -1) return text;

    const before = text.slice(0, matchIndex);
    const match = text.slice(matchIndex, matchIndex + inputValue.length);
    const after = text.slice(matchIndex + inputValue.length);

    return (
      <>
        {before}
        <span className="autocomplete-match">{match}</span>
        {after}
      </>
    );
  };

  return (
    <div
      ref={wrapperRef}
      className={`autocomplete-dropdown ${className} ${disabled ? 'disabled' : ''}`}
    >
      <div className="autocomplete-input-wrapper">
        {/* Ghost text layer - only show suggestion when focused */}
        {isFocusedRef.current && ghostText && (
          <div className="autocomplete-ghost" aria-hidden="true">
            <span className="ghost-typed">{inputValue}</span>
            <span className="ghost-suggestion">{ghostText}</span>
          </div>
        )}

        {/* Actual input */}
        <input
          ref={inputRef}
          type="text"
          className="autocomplete-input"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete="off"
          {...rest}
        />

        {/* Dropdown arrow */}
        <div
          className="autocomplete-arrow"
          onClick={() => {
            if (disabled) return;
            // Focus input and update ref so dropdown can show
            inputRef.current?.focus();
            isFocusedRef.current = true;
            setIsOpen(!isOpen);
          }}
        >
          <i className={`fas fa-caret-${isOpen ? 'up' : 'down'}`} />
        </div>
      </div>

      {/* Dropdown list - only show when focused and has suggestions */}
      {isOpen && isFocusedRef.current && document.activeElement === inputRef.current && (loading || sortedSuggestions.length > 0) && (
        <ul ref={listRef} className="autocomplete-list scrollbar">
          {loading ? (
            <li className="autocomplete-loading">Loading...</li>
          ) : (
            sortedSuggestions.map((suggestion, index) => (
              <li
                key={suggestion}
                className={`autocomplete-item ${index === highlightedIndex ? 'highlighted' : ''}`}
                onMouseDown={(e) => e.preventDefault()} // Prevent blur
                onClick={() => handleItemClick(suggestion)}
                onMouseEnter={() => handleItemMouseEnter(index)}
              >
                {renderHighlightedText(suggestion)}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default AutocompleteDropdown;
